'use client';

// import MarkerClusterGroup from 'react-leaflet-markercluster';
// import L, { MarkerCluster } from 'leaflet';
import L from 'leaflet';
import type React from 'react';
import 'leaflet/dist/leaflet.css';
// import '@maplibre/maplibre-gl-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { isNil } from 'lodash-es';
// import 'react-leaflet-markercluster/styles';
import { type Ref, useEffect, useMemo, useRef } from 'react';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvents,
} from 'react-leaflet';

export type MarkerProps = {
  id: string;
  active: boolean;
  lat: number;
  lng: number;
};

interface MapProps {
  id: string;
  center?: number[];
  markers?: MarkerProps[];
  zoom?: number;
  styled?: string;
  readOnly?: boolean;
  zoomControl?: boolean;
  scrollWheelZoom?: boolean;
  ref?: any;
  autoFit?: boolean;
  onMarkerClick?: (markerId: string) => void;
  onMapClick?: (e: any) => void;
  onMoveEnd?: (bounds: L.LatLngBounds) => void;
  markerPopup?: (data: any) => any;
}

interface MapComponentProps {
  id: string;
  ref: any;
  center?: number[];
  zoom?: number;
  markers?: MarkerProps[];
  markerRef: any;
  readOnly?: boolean;
  autoFit?: boolean;
  onMapClick?: (event: any) => void;
  onMoveEnd?: (bounds: L.LatLngBounds) => void;
}

const ZOOM_IN = 4;
const NO_ZOOM = 2;
const DEFAULT_PADDING_RATIO = 0.5;

const MapComponent: React.FC<MapComponentProps> = ({
  id,
  center,
  zoom,
  readOnly,
  markers,
  markerRef,
  autoFit,
  onMapClick,
  onMoveEnd,
}) => {
  const map = useMapEvents({
    click(event) {
      if (onMapClick) {
        onMapClick(event);
      }
    },
    moveend() {
      if (onMoveEnd) {
        try {
          const bounds: L.LatLngBounds = map.getBounds();
          if (bounds) {
            onMoveEnd(bounds);
          }
        } catch (err) {
          console.error(err);
        }
      }
    },
  });

  /*
  L.maplibreGL({
    style: 'https://tiles.openfreemap.org/styles/bright',
  }).addTo(map);
*/

  useEffect(() => {
    if (!isNil(center) && !isNil(zoom)) {
      map.invalidateSize();
      map.setView([center[0], center[1]], zoom);
    }
  }, [center, map.setView, map.invalidateSize, zoom]);

  useEffect(() => {
    if (readOnly) {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    }
  }, [map, readOnly]);

  useEffect(() => {
    if (map && !isNil(markers) && center && markers.length > 0) {
      if (!isNil(autoFit) && autoFit === true) {
        map.setView([center[0], center[1]], 13);

        const bounds = map.getBounds();

        for (const marker of markers) {
          bounds.extend([marker.lat, marker.lng]);
        }

        map.invalidateSize();
        map.fitBounds(bounds.pad(DEFAULT_PADDING_RATIO));
      }
    }
  }, [map, markers, autoFit, center]);

  return null;
};

const CustomMap: React.FC<MapProps> = ({
  id,
  center,
  zoom: customZoom,
  styled: customClassName,
  readOnly,
  zoomControl,
  scrollWheelZoom,
  ref,
  markers,
  autoFit,
  onMarkerClick,
  onMapClick,
  onMoveEnd,
  markerPopup,
}) => {
  const createMarker = useMemo(
    () =>
      new L.Icon({
        iconUrl: markerIcon,
        iconRetinaUrl: markerIcon2x.src,
        iconSize: [26, 41],
        iconAnchor: [13, 41],
        shadowUrl: markerShadow.src,
      }),
    [],
  );

  /*
  const createClusterCustomIcon = (cluster: MarkerCluster) =>
    L.divIcon({
      html: `<span>${cluster.getChildCount()}</span>`,
      className: 'custom-marker-cluster-icon',
      iconSize: L.point(33, 33, true),
    });
    */

  const markerRef: Ref<any> = useRef(null);
  const mapRef: Ref<any> = useRef(null);

  const markersGroup = useMemo(() => {
    if (!isNil(markers) && markers.length > 0) {
      return (
        /*
        <MarkerClusterGroup
          iconCreateFunction={createClusterCustomIcon}
          chunkedLoading
          showCoverageOnHover={false}
          ref={markerRef}
        >
          */
        <>
          {markers.map((marker) =>
            !isNil(onMarkerClick) ? (
              <Marker
                data-test-id={`map-marker-${marker.id}`}
                key={`map-marker-${marker.id}`}
                position={[marker.lat, marker.lng] as L.LatLngExpression}
                icon={createMarker}
                zIndexOffset={marker.active ? 10 : 1}
                eventHandlers={{
                  click: (e) => {
                    onMarkerClick(marker.id);
                  },
                }}
              >
                {markerPopup && <Popup>{markerPopup(marker)}</Popup>}
              </Marker>
            ) : (
              <Marker
                data-test-id={`map-marker-${marker.id}`}
                key={`map-marker-${marker.id}`}
                position={[marker.lat, marker.lng] as L.LatLngExpression}
                icon={createMarker}
                zIndexOffset={marker.active ? 10 : 1}
              >
                {markerPopup && <Popup>{markerPopup(marker)}</Popup>}
              </Marker>
            ),
          )}
        </>
        /*
        </MarkerClusterGroup>
        */
      );
    }

    return null;
  }, [markers, createMarker, markerPopup, onMarkerClick]);

  return (
    <MapContainer
      ref={ref}
      id={id}
      center={(center as L.LatLngExpression) || [51, -0.09]}
      zoom={customZoom ? (center ? ZOOM_IN : NO_ZOOM) : undefined}
      scrollWheelZoom={readOnly ? false : scrollWheelZoom}
      zoomControl={readOnly ? false : zoomControl}
      dragging={!readOnly}
      className={`h-full ${customClassName || ''}`}
    >
      <MapComponent
        id={`${id}-component`}
        ref={mapRef}
        markers={markers}
        markerRef={markerRef}
        center={center}
        zoom={customZoom}
        autoFit={autoFit}
        onMapClick={onMapClick}
        onMoveEnd={onMoveEnd}
      />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {markersGroup}
    </MapContainer>
  );
};

export default CustomMap;
